/* eslint-disable no-unused-vars,react/prop-types */
import moment from 'moment';
import PropTypes from 'prop-types';
import { equals, not } from 'ramda';
import React, {
  useState, useRef, useEffect,
} from 'react';
import { Link } from 'react-router-dom';

import ModalView from '@/containers/task/components/timeEntry/timeEntryCRUD/modalView';
import rules from '@/containers/task/components/timeEntry/timeEntryCRUD/rules';
import { ModuleTaskItemDropdown } from '../../../../components/moduleSelect';
import './style.sass';
import { ACTIONS, MODULES } from '../../../../constants/pemissions';
import { IMAGES_SIZE } from '../../../../constants/ui';
import {
  ButtonsCollection,
  StatusBar,
  LabelPriorityTask,
  BrillMask,
  Avatar,
  Grid,
  DropDown,
} from '../../../../ui-kit';
import { getTimeUnitsFromTimeString, getSecondsFromTimeString } from '../../../../utils/helpers/dateHelpers';
import { getImageUrl } from '../../../../utils/helpers/requestHelpers';
import { useBulkActions } from '../bulkActions';

const isHasTime = (value) => not(equals(value, '0m'));

const TaskItem = ({
  assigneeAvatar,
  id,
  isItemDrag,
  onEdit,
  onCopy,
  onDelete,
  classNameStatus,
  assigneeName,
  estimatedTime,
  estimatedHours,
  spentTime,
  progress,
  author,
  authorName,
  data,
  onOpenProfileAuthor,
  t,
  onPin,
  onUnpin,
  isTaskPin,
  P,
  assigneeMembers,
  onChangeAssignee,
  statuses,
  onChangeStatus,
  modules,
  projectId,
  onChangeModule,
  moduleName,
  addModuleIsPending,
  onChangeEstimatedTime,
  onAddTimeEntry,
  isAddTimeEntryPending,
}) => {
  const { isTaskSelected, toggleTask } = useBulkActions();
  const [isEditingEstimated, setIsEditingEstimated] = useState(false);
  const [estimatedValue, setEstimatedValue] = useState(estimatedHours);
  const [isTimeEntryModalOpen, setIsTimeEntryModalOpen] = useState(false);
  const [timeEntryValues, setTimeEntryValues] = useState({ date: new Date(), time: '' });
  const [timeEntryErrors, setTimeEntryErrors] = useState({ date: '', time: '' });
  const inputRef = useRef(null);
  const isSavingEstimatedRef = useRef(false);

  useEffect(() => {
    if (isEditingEstimated && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditingEstimated]);

  const handleEstimatedClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    isSavingEstimatedRef.current = false;
    setEstimatedValue(estimatedHours);
    setIsEditingEstimated(true);
  };

  const saveEstimatedValue = () => {
    if (isSavingEstimatedRef.current) return;
    isSavingEstimatedRef.current = true;
    setIsEditingEstimated(false);
    const value = getTimeUnitsFromTimeString(estimatedValue, 'h');
    if (!Number.isNaN(value) && value >= 0) {
      onChangeEstimatedTime(value);
    }
  };

  const handleEstimatedSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    saveEstimatedValue();
  };

  const handleEstimatedBlur = () => {
    setIsEditingEstimated(false);
  };

  const handleEstimatedKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      saveEstimatedValue();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setIsEditingEstimated(false);
    }
  };

  const handleEstimatedChange = (e) => {
    setEstimatedValue(e.target.value);
  };

  const handleOpenTimeEntryModal = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setTimeEntryValues({ date: new Date(), time: '' });
    setTimeEntryErrors({ date: '', time: '' });
    setIsTimeEntryModalOpen(true);
  };

  const handleCloseTimeEntryModal = () => {
    setIsTimeEntryModalOpen(false);
  };

  const handleTimeEntryChange = ({ target: { name, value } }) => {
    setTimeEntryValues(prev => ({ ...prev, [name]: value }));
    if (timeEntryErrors[name]) {
      setTimeEntryErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleTimeEntrySubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    rules.validate(timeEntryValues, { abortEarly: false })
      .then(() => {
        onAddTimeEntry({
          projectId: data.project_id,
          clientId: data.client_id,
          taskId: id,
          date: moment(timeEntryValues.date).format('YYYY-MM-DD'),
          time: getSecondsFromTimeString(timeEntryValues.time, 'h'),
        });
        setIsTimeEntryModalOpen(false);
      })
      .catch((error) => {
        const errors = {};
        error.inner.forEach((err) => {
          errors[err.path] = err.message;
        });
        setTimeEntryErrors(errors);
      });
  };

  const renderEstimatedContent = () => {
    if (isEditingEstimated) {
      return (
        <div className="project-task__estimation-edit">
          <input
            ref={inputRef}
            className="project-task__estimation-input"
            value={estimatedValue}
            onChange={handleEstimatedChange}
            onBlur={handleEstimatedBlur}
            onKeyDown={handleEstimatedKeyDown}
            onClick={(e) => {
              e.stopPropagation();
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            step="0.01"
            min="0"
          />
          <button
            type="button"
            className="project-task__estimation-submit"
            onMouseDown={handleEstimatedSubmit}
            onClick={handleEstimatedSubmit}
            title={t('Save')}
          >
            <span className="icon-ok" />
          </button>
        </div>
      );
    }

    const displayValue = isHasTime(estimatedTime) ? estimatedTime : '0h';

    return P(MODULES.TASKS, ACTIONS.UPDATE, (
      <b
        className="project-task__estimation project-task__estimation--editable"
        onClick={handleEstimatedClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            handleEstimatedClick(e);
          }
        }}
      >
        {displayValue}
      </b>
    )) || <b className="project-task__estimation">{displayValue}</b>;
  };

  return (
    <Link to={`task/${id}/`} style={{ textDecoration: 'none' }}>
      <Grid.Row
        className={`project-task ${classNameStatus} ${isItemDrag ? 'project-task--drag' : ''
          } project-task__cursor`}
      >
        <Grid.ColData className="project-task__col--checkbox">
          <span
            role="checkbox"
            aria-checked={isTaskSelected(id)}
            tabIndex={0}
            className={`project-task__checkbox ${isTaskSelected(id) ? 'project-task__checkbox--checked' : ''}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggleTask(id);
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                toggleTask(id);
              }
            }}
          />
        </Grid.ColData>
        <Grid.ColData className="project-task__col--id">
          <div>{data.id}</div>
        </Grid.ColData>
        <Grid.ColData className="project-task__col--module">
          {P(MODULES.TASKS, ACTIONS.UPDATE, (
            <ModuleTaskItemDropdown
              modules={modules}
              currentModuleId={data.project_module_id}
              onChange={onChangeModule}
              projectId={projectId}
              isPending={addModuleIsPending}
            />
          )) || (
              <span className="project-task__module-name" title={moduleName}>
                {moduleName || t('None')}
              </span>
            )}
        </Grid.ColData>
        <Grid.ColData className="project-task__col--subject">
          <span title={data.title} className="project-task__title-name-task text--cut">{data.title}</span>
        </Grid.ColData>
        <Grid.ColData className="project-task__col--priority">
          <LabelPriorityTask className="project-task__priority-label" status={data.priority} />
        </Grid.ColData>
        <Grid.ColData className="project-task__col--status">
          <DropDown
            className="project-task__status-dropdown"
            name="status-dropdown"
            label=""
            list={statuses}
            currentItem={data.status_id}
            onChange={onChangeStatus}
          />
        </Grid.ColData>
        <Grid.ColData className="project-task__col--author">
          {author && (
            <BrillMask
              asLink
              onClick={(e) => {
                e.stopPropagation();
                onOpenProfileAuthor();
              }}
            >
              <Avatar src={getImageUrl(author.avatar, IMAGES_SIZE.xs)} alt={authorName} />
            </BrillMask>
          )}
        </Grid.ColData>
        <Grid.ColData className="project-task__col--assignee">
          <DropDown
            className="project-task__assignee-dropdown"
            name="assignee-dropdown"
            label={
              <BrillMask>
                <Avatar src={assigneeAvatar} alt={assigneeName} />
              </BrillMask>
            }
            list={assigneeMembers}
            currentItem={data.assigneeUser}
            onChange={onChangeAssignee}
          />
        </Grid.ColData>
        <Grid.ColData className="project-task__col--estimation">
          {renderEstimatedContent()}
        </Grid.ColData>
        <Grid.ColData className="project-task__col--spent">
          {isHasTime(spentTime) && <b className="project-task__spent">{spentTime}</b>}
          <ButtonsCollection.ButtonIcons
            className="project-task__spent-add-button"
            title={t('Add time entry')}
            onClick={handleOpenTimeEntryModal}
          >
            <span className="icon-timer-2-icon" />
          </ButtonsCollection.ButtonIcons>
        </Grid.ColData>
        <Grid.ColData className="project-task__col--progress">
          <StatusBar percents={progress} />
        </Grid.ColData>
        <Grid.ColData className="project-task__col--controls">
          <DropDown
            className="controls-group-task"
            name="controls-group-task"
            label={
              <ButtonsCollection.ButtonIcons
                className="project-task__controls-button-task"
                title="Controls for sprint"
              >
                <span className="project-task__control-icon icon-settings-vertical">
                  <span className="path1" />
                  <span className="path2" />
                  <span className="path3" />
                </span>
              </ButtonsCollection.ButtonIcons>
            }
            list={[
              {
                id: 0,
                onClick: onEdit,
                label: (
                  <>
                    <span className="controls-group-task__icon icon-pencil-2" />
                    <span className="controls-group-task__text">{t('Edit')}</span>
                  </>
                ),
              },
              {
                id: 1,
                onClick: isTaskPin ? onUnpin : onPin,
                label: (
                  <>
                    <span className="controls-group-task__icon icon-pin" />
                    <span className="controls-group-task__text">
                      {isTaskPin ? 'Unpin' : t('Pin')}
                    </span>
                  </>
                ),
              },
              ...P(MODULES.TASKS, ACTIONS.DELETE, [
                {
                  id: 2,
                  onClick: onDelete,
                  label: (
                    <>
                      <span className="controls-group-task__icon icon-delete-icon" />
                      <span className="controls-group-task__text">{t('Delete')}</span>
                    </>
                  ),
                },
              ]),
              {
                id: 3,
                onClick: onCopy,
                label: (
                  <>
                    <span className="controls-group-task__icon icon-link" />
                    <span className="controls-group-task__text">{t('Copy')}</span>
                  </>
                ),
              },
            ]}
          />
        </Grid.ColData>
      </Grid.Row>
      <div
        role="presentation"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <ModalView
          isOpen={isTimeEntryModalOpen}
          onCloseModal={handleCloseTimeEntryModal}
          handleSubmit={handleTimeEntrySubmit}
          handleChange={handleTimeEntryChange}
          values={timeEntryValues}
          errors={timeEntryErrors}
          isPending={isAddTimeEntryPending}
          title={t('Add time entry')}
          titleAction={t('Add')}
          t={t}
        />
      </div>
    </Link>
  );
};

TaskItem.propTypes = {
  onDelete: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  isItemDrag: PropTypes.bool.isRequired,
  id: PropTypes.number.isRequired,
  P: PropTypes.func.isRequired,
  agendaColumn: PropTypes.instanceOf(Object).isRequired,
  modules: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
  })).isRequired,
  projectId: PropTypes.number.isRequired,
  onChangeModule: PropTypes.func.isRequired,
  onChangeEstimatedTime: PropTypes.func.isRequired,
  onAddTimeEntry: PropTypes.func.isRequired,
  isAddTimeEntryPending: PropTypes.bool.isRequired,
  estimatedHours: PropTypes.number.isRequired,
  moduleName: PropTypes.string,
  addModuleIsPending: PropTypes.bool,
};

TaskItem.defaultProps = {
  moduleName: '',
  addModuleIsPending: false,
};

export default TaskItem;
